import { Head, Link, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface Stats {
  total_users: number
  total_accounts: number
  total_listings: number
  total_videos: number
  total_renders: number
  completed_renders: number
  users_today: number
  users_this_week: number
  accounts_this_week: number
  listings_this_week: number
  videos_this_week: number
  active_jobs: number
  failed_jobs: number
}

interface DailyActivity {
  date: string
  users: number
  listings: number
  videos: number
  renders: number
}

interface StatusBreakdown {
  name: string
  value: number
}

interface TopAccount {
  name: string
  listings: number
  videos: number
}

interface ActivityItem {
  kind: "listing" | "video" | "render"
  item_id: string
  label: string
  account: string
  user: string
  status: string
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface ActivityFilters {
  search: string | null
  kind: string | null
  status: string | null
  sort: string
  direction: string
}

interface RecentActivity {
  items: ActivityItem[]
  pagination: PaginationMeta
  filters: ActivityFilters
}

interface Props {
  stats: Stats
  activity: DailyActivity[]
  listing_statuses: StatusBreakdown[]
  video_statuses: StatusBreakdown[]
  render_statuses: StatusBreakdown[]
  top_accounts: TopAccount[]
  recent_activity: RecentActivity
}

// --- Palette ---

const STATUS_PALETTE: Record<string, string> = {
  draft: "#D4A853",
  active: "#34D399",
  archived: "#6B6B6B",
  scripted: "#60A5FA",
  rendered: "#34D399",
  published: "#10B981",
  pending: "#6B6B6B",
  rendering: "#D4A853",
  completed: "#34D399",
  failed: "#C41E2A",
  timed_out: "#C41E2A",
}

const ACTIVITY_COLOURS = {
  users: "#C41E2A",
  listings: "#D4A853",
  videos: "#60A5FA",
  renders: "#34D399",
}

const KIND_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  listing: { bg: "bg-gold/10", text: "text-gold", label: "Listing" },
  video: { bg: "bg-blue-400/10", text: "text-blue-400", label: "Video" },
  render: { bg: "bg-emerald-400/10", text: "text-emerald-400", label: "Render" },
}

// --- Component ---

export default function AdminHome({
  stats,
  activity,
  listing_statuses,
  video_statuses,
  render_statuses,
  top_accounts,
  recent_activity,
}: Props) {
  const renderRate =
    stats.total_renders > 0
      ? Math.round((stats.completed_renders / stats.total_renders) * 100)
      : 0

  return (
    <AdminLayout>
      <Head title="Dashboard | Admin" />
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-display font-semibold text-cream">Overview</h1>
          <p className="mt-1 text-sm text-cream/50">Platform health at a glance</p>
        </div>

        {/* Stat cards — top row */}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <StatCard label="Accounts" value={stats.total_accounts} sub={`+${stats.accounts_this_week} this week`} href="/admin/accounts" />
          <StatCard label="Users" value={stats.total_users} sub={`+${stats.users_this_week} this week`} href="/admin/users" />
          <StatCard label="Listings" value={stats.total_listings} sub={`+${stats.listings_this_week} this week`} href="/admin/listings" />
          <StatCard label="Videos" value={stats.total_videos} sub={`+${stats.videos_this_week} this week`} href="/admin/videos" />
          <StatCard label="Renders" value={stats.total_renders} sub={`${renderRate}% success`} accent={renderRate < 80 ? "warn" : undefined} />
        </div>

        {/* Secondary stats */}
        <div className="grid gap-3 grid-cols-3">
          <MiniStat label="Users today" value={stats.users_today} />
          <MiniStat label="Active jobs" value={stats.active_jobs} href="/admin/jobs" />
          <MiniStat label="Failed jobs" value={stats.failed_jobs} href="/admin/jobs?tab=failed" alert={stats.failed_jobs > 0} />
        </div>

        {/* Activity feed — full-width with search/filter/sort/pagination */}
        <ActivityFeed data={recent_activity} />

        {/* Activity chart */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-medium uppercase tracking-wider text-cream/40">
              30-day activity
            </h2>
            <div className="flex gap-4">
              {(Object.entries(ACTIVITY_COLOURS) as [string, string][]).map(([key, colour]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: colour }} />
                  <span className="text-[11px] capitalize text-cream/40">{key}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activity} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C41E2A" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#C41E2A" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gListings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4A853" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#D4A853" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVideos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gRenders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(245,242,237,0.04)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(245,242,237,0.3)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: "rgba(245,242,237,0.3)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="users" stroke="#C41E2A" fill="url(#gUsers)" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="listings" stroke="#D4A853" fill="url(#gListings)" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="videos" stroke="#60A5FA" fill="url(#gVideos)" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="renders" stroke="#34D399" fill="url(#gRenders)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status breakdowns + top accounts */}
        <div className="grid gap-3 lg:grid-cols-3">
          <DonutCard title="Listings" data={listing_statuses} />
          <DonutCard title="Videos" data={video_statuses} />
          <DonutCard title="Renders" data={render_statuses} />
        </div>

        {/* Top accounts */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            Top accounts
          </h2>
          {top_accounts.length === 0 ? (
            <p className="text-sm text-cream/30">No accounts yet.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={top_accounts}
                  layout="vertical"
                  margin={{ top: 0, right: 4, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke="rgba(245,242,237,0.04)" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: "rgba(245,242,237,0.3)", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: "rgba(245,242,237,0.5)", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={100}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={false} />
                  <Bar dataKey="listings" fill="#D4A853" radius={[0, 3, 3, 0]} barSize={10} name="Listings" />
                  <Bar dataKey="videos" fill="#60A5FA" radius={[0, 3, 3, 0]} barSize={10} name="Videos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  )
}

// --- Sub-components ---

function StatCard({
  label,
  value,
  sub,
  href,
  accent,
}: {
  label: string
  value: number
  sub: string
  href?: string
  accent?: "warn"
}) {
  const inner = (
    <div
      className={`group relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.03] p-5 transition-colors ${href ? "hover:bg-white/[0.06] cursor-pointer" : ""}`}
    >
      <p className="text-xs font-medium uppercase tracking-wider text-cream/40">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-cream">
        {value.toLocaleString()}
      </p>
      <p className={`mt-1 text-xs ${accent === "warn" ? "text-red/70" : "text-cream/30"}`}>
        {sub}
      </p>
      {/* Decorative corner */}
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-white/[0.02] transition-transform duration-300 group-hover:scale-150" />
    </div>
  )

  if (href) {
    return (
      <Link href={href} className="block">
        {inner}
      </Link>
    )
  }
  return inner
}

function MiniStat({
  label,
  value,
  href,
  alert,
}: {
  label: string
  value: number
  href?: string
  alert?: boolean
}) {
  const inner = (
    <div
      className={`rounded-md border px-4 py-3 transition-colors ${
        alert
          ? "border-red/20 bg-red/5"
          : "border-white/[0.06] bg-white/[0.02]"
      } ${href ? "hover:bg-white/[0.05] cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-cream/40">
          {label}
        </span>
        <span
          className={`text-lg font-semibold tabular-nums ${alert ? "text-red" : "text-cream/80"}`}
        >
          {value.toLocaleString()}
        </span>
      </div>
    </div>
  )

  if (href) {
    return (
      <Link href={href} className="block">
        {inner}
      </Link>
    )
  }
  return inner
}

function DonutCard({ title, data }: { title: string; data: StatusBreakdown[] }) {
  const total = data.reduce((sum, d) => sum + d.value, 0)

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-cream/40">
        {title}
      </h2>
      {total === 0 ? (
        <p className="py-8 text-center text-sm text-cream/30">No data</p>
      ) : (
        <div className="flex items-center gap-6">
          <div className="size-28 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={50}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {data.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={STATUS_PALETTE[entry.name] || "#6B6B6B"}
                    />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1.5">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: STATUS_PALETTE[entry.name] || "#6B6B6B" }}
                  />
                  <span className="capitalize text-cream/60">{entry.name.replace("_", " ")}</span>
                </div>
                <span className="tabular-nums text-cream/40">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-md border border-white/10 bg-charcoal/95 px-3 py-2 shadow-xl backdrop-blur-sm">
      {label && <p className="mb-1 text-[11px] font-medium text-cream/50">{label}</p>}
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="capitalize text-cream/60">{entry.name}</span>
          <span className="ml-auto tabular-nums text-cream">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

const KIND_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "listing", label: "Listings" },
  { value: "video", label: "Videos" },
  { value: "render", label: "Renders" },
] as const

const ACTIVITY_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "scripted", label: "Scripted" },
  { value: "rendered", label: "Rendered" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "pending", label: "Pending" },
  { value: "rendering", label: "Rendering" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
] as const

const ACTIVITY_COLUMNS = [
  { key: "kind", label: "Type", sortable: true },
  { key: "label", label: "Name", sortable: true },
  { key: "account", label: "Account", sortable: true },
  { key: "user", label: "User", sortable: false },
  { key: "status", label: "Status", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
] as const

function ActivityFeed({ data }: { data: RecentActivity }) {
  const { items, pagination, filters } = data
  const [searchValue, setSearchValue] = useState(filters.search || "")

  const buildActivityParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (filters.search) p.activity_search = filters.search
      if (filters.kind) p.activity_kind = filters.kind
      if (filters.status) p.activity_status = filters.status
      if (filters.sort !== "created_at") p.activity_sort = filters.sort
      if (filters.direction !== "desc") p.activity_direction = filters.direction
      for (const [k, v] of Object.entries(overrides)) {
        if (v === undefined || v === "") {
          delete p[k]
        } else {
          p[k] = v
        }
      }
      return p
    },
    [filters],
  )

  function navigate(overrides: Record<string, string | undefined>) {
    router.get("/admin", buildActivityParams({ ...overrides, activity_page: undefined }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    navigate({ activity_search: searchValue || undefined })
  }

  function handleSort(column: string) {
    const newDirection = filters.sort === column && filters.direction === "asc" ? "desc" : "asc"
    navigate({ activity_sort: column, activity_direction: newDirection })
  }

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-cream/40">
          Activity feed
          <span className="ml-2 text-cream/25">({pagination.count})</span>
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={filters.kind || "all"}
            onValueChange={(v) => navigate({ activity_kind: v === "all" ? undefined : v })}
          >
            <SelectTrigger className="h-8 w-[120px] border-white/10 bg-white/5 text-xs text-cream">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark border-white/10 bg-charcoal">
              {KIND_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.status || "all"}
            onValueChange={(v) => navigate({ activity_status: v === "all" ? undefined : v })}
          >
            <SelectTrigger className="h-8 w-[130px] border-white/10 bg-white/5 text-xs text-cream">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark border-white/10 bg-charcoal">
              {ACTIVITY_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <form onSubmit={handleSearch} className="flex gap-1.5">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-cream/30" />
              <Input
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8 w-44 border-white/10 bg-white/5 pl-7 text-xs text-cream placeholder:text-cream/30"
              />
            </div>
            {filters.search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-cream/50 hover:text-cream"
                onClick={() => {
                  setSearchValue("")
                  navigate({ activity_search: undefined })
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-white/[0.06]">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
              {ACTIVITY_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40"
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 transition-colors hover:text-cream/70"
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                      <SortIcon
                        active={filters.sort === col.key}
                        direction={filters.sort === col.key ? filters.direction : undefined}
                      />
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-cream/30">
                  {filters.search
                    ? `No activity matching "${filters.search}"`
                    : "No activity found"}
                </td>
              </tr>
            )}
            {items.map((item, i) => {
              const style = KIND_STYLES[item.kind]
              const href =
                item.kind === "listing"
                  ? `/admin/listings/${item.item_id}`
                  : `/admin/videos/${item.item_id}`

              return (
                <tr
                  key={i}
                  className="cursor-pointer border-b border-white/[0.04] text-cream/60 transition-colors hover:bg-white/[0.04]"
                  onClick={() => router.visit(href)}
                >
                  <td className="whitespace-nowrap px-3 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${style.bg} ${style.text}`}
                    >
                      {style.label}
                    </span>
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-cream/70">
                    {item.label}
                  </td>
                  <td className="max-w-[120px] truncate whitespace-nowrap px-3 py-2 text-cream/40">
                    {item.account}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-cream/40">{item.user}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <Badge
                      className="text-[10px]"
                      style={{
                        backgroundColor: `${STATUS_PALETTE[item.status] || "#6B6B6B"}20`,
                        color: STATUS_PALETTE[item.status] || "#6B6B6B",
                        borderColor: `${STATUS_PALETTE[item.status] || "#6B6B6B"}33`,
                      }}
                    >
                      {item.status.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-cream/30">
                    {formatRelative(item.created_at)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="mt-3">
          <Pagination
            baseUrl="/admin"
            params={buildActivityParams()}
            pagination={pagination}
            pageParam="activity_page"
          />
        </div>
      )}
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg
        className="size-3 opacity-30"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
        />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  )
}

// --- Helpers ---

function formatRelative(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "now"
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}
